import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Recording, RecordingSearchMatch, RecordingSearchResult, RecordingSearchStatus } from '../types';
import ApiService from '../services/apiService';
import { TagFilterOption, getTagDisplayName } from './RecordingsTagFilter';
import { toDateKey } from './RecordingsCalendar';

const MIN_QUERY_LENGTH = 1;
const DEBOUNCE_MS = 300;
const BUILDING_POLL_MS = 5000;
const RESULT_LIMIT = 20;
const MAX_VISIBLE_SNIPPETS = 2;
const MAX_TAG_SUGGESTIONS = 8;

interface RecordingsSearchProps {
    apiService: ApiService;
    fullTextEnabled: boolean;
    fallbackRecordings: Recording[];
    // Open/dicht wordt door de pagina beheerd (zoekicoon in de paginakop)
    isOpen: boolean;
    onClose: () => void;
    onOpenRecording: (recording: Recording) => void;
    // Meldt of er een actieve zoekterm staat, zodat de pagina de gewone lijst kan verbergen
    onActiveChange: (active: boolean) => void;
    // Tagfilter: de zoekterm werkt als autocomplete over deze tags; selectie leeft op de pagina
    tagOptions: TagFilterOption[];
    selectedTagIds: number[];
    onToggleTag: (tagId: number) => void;
    // Kalenderfilter: de geselecteerde dag beperkt ook de zoekresultaten, en via
    // onResultsChange volgt de kalender op de pagina de gevonden opnames
    selectedDate: string | null;
    selectedDayLabel: string | null;
    onClearDate: () => void;
    onResultsChange: (recordings: Recording[] | null) => void;
}

// Verslagen en documenten zijn markdown; in een snippet-fragment willen we alleen de
// kale tekst tonen, zonder **, ##, opsommingstekens of linksyntax.
const stripMarkdown = (text: string) =>
    text
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/`+/g, '')
        .replace(/\*\*+|__+/g, '')
        .replace(/(^|\s)#{1,6}(?=\s)/g, '$1')
        .replace(/(^|\s)[-*•](?=\s)/g, '$1')
        .replace(/\s+/g, ' ');

// Snippets bevatten alleen <mark>…</mark>-markering van de server. We splitsen daarop
// en zetten de delen als text nodes, zodat overige <>-tekens uit transcripties nooit
// als HTML geïnterpreteerd worden.
const renderSnippet = (snippet: string) => {
    const parts = snippet.split(/<mark>([\s\S]*?)<\/mark>/g);
    return parts.map((part, index) =>
        index % 2 === 1 ? (
            <mark
                key={index}
                className="rounded-sm font-semibold"
                style={{ background: 'var(--warning-bg)', color: 'var(--text-strong)', padding: '0 1px' }}
            >
                {stripMarkdown(part)}
            </mark>
        ) : (
            <React.Fragment key={index}>{stripMarkdown(part)}</React.Fragment>
        )
    );
};

const RecordingsSearch: React.FC<RecordingsSearchProps> = ({ apiService, fullTextEnabled, fallbackRecordings, isOpen, onClose, onOpenRecording, onActiveChange, tagOptions, selectedTagIds, onToggleTag, selectedDate, selectedDayLabel, onClearDate, onResultsChange }) => {
    const { t, i18n } = useTranslation();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<RecordingSearchResult[] | null>(null);
    const [indexStatus, setIndexStatus] = useState<RecordingSearchStatus>('ready');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<'unavailable' | 'generic' | null>(null);

    const trimmedQuery = query.trim();
    const isActive = isOpen && trimmedQuery.length >= MIN_QUERY_LENGTH;

    // Tag-autocomplete: de zoekterm filtert de tagsuggesties al vanaf het eerste teken.
    // Zonder zoekterm tonen we de meest gebruikte tags als snelkeuze; bij heel veel
    // tags blijft de lijst beperkt tot MAX_TAG_SUGGESTIONS met een "+N meer"-teller.
    const selectedTags = tagOptions
        .filter(option => selectedTagIds.includes(option.tag.id))
        .map(option => option.tag);
    const tagQuery = trimmedQuery.toLowerCase();
    const matchingTagOptions = tagOptions
        .filter(option => !selectedTagIds.includes(option.tag.id))
        .filter(option => tagQuery === '' || option.tag.name.toLowerCase().includes(tagQuery))
        .sort((a, b) => b.count - a.count || a.tag.name.localeCompare(b.tag.name));
    const tagSuggestions = matchingTagOptions.slice(0, MAX_TAG_SUGGESTIONS);
    const hiddenTagCount = matchingTagOptions.length - tagSuggestions.length;

    const localResults = useMemo(() => {
        const query = trimmedQuery.toLocaleLowerCase();
        return fallbackRecordings.filter(recording => [recording.name, ...(recording.tags ?? []).map(tag => tag.name)]
            .some(text => text?.toLocaleLowerCase().includes(query)))
            .map(recording => ({ recording, matches: [] }));
    }, [trimmedQuery, fallbackRecordings]);
    const availableResults = !fullTextEnabled || trimmedQuery.length < 2 || indexStatus === 'building' || searchError ? localResults : results;

    // Actieve tagfilters gelden ook binnen de full-text resultaten
    const visibleResults = useMemo(() => {
        if (availableResults === null || selectedTagIds.length === 0) return availableResults;
        return availableResults.filter(result => result.recording.tags?.some(tag => selectedTagIds.includes(tag.id)));
    }, [availableResults, selectedTagIds]);

    // De kalender op de pagina volgt de zoekresultaten. Bewust vóór het dagfilter,
    // anders zouden de badges van alle andere dagen verdwijnen zodra je een dag kiest.
    const resultRecordings = useMemo(
        () => (isActive && visibleResults !== null ? visibleResults.map(result => result.recording) : null),
        [isActive, visibleResults]
    );
    useEffect(() => {
        onResultsChange(resultRecordings);
    }, [resultRecordings, onResultsChange]);
    useEffect(() => () => onResultsChange(null), [onResultsChange]);

    // Een geselecteerde kalenderdag beperkt de getoonde resultaten tot die dag
    const displayResults = useMemo(() => {
        if (visibleResults === null || !selectedDate) return visibleResults;
        return visibleResults.filter(result => {
            const date = new Date(result.recording.recordingDateTime ?? result.recording.createdAt);
            return !Number.isNaN(date.getTime()) && toDateKey(date) === selectedDate;
        });
    }, [visibleResults, selectedDate]);

    const abortRef = useRef<AbortController | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    const closeSearch = () => {
        setQuery('');
        onClose();
        requestAnimationFrame(() => (document.querySelector('[data-search-toggle]') as HTMLButtonElement | null)?.focus());
    };

    useEffect(() => {
        onActiveChange(isActive);
    }, [isActive, onActiveChange]);

    const runSearch = useCallback(async (term: string, silent: boolean) => {
        // Laatste request wint: lopende zoekopdracht annuleren
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        if (!silent) {
            setIsSearching(true);
        }

        try {
            const response = await apiService.searchRecordings(term, RESULT_LIMIT, 'date_desc', controller.signal);
            if (controller.signal.aborted) return;
            setResults(response.results);
            setIndexStatus(response.status);
            setSearchError(null);
        } catch (error) {
            if (controller.signal.aborted) return;
            const status = (error as any)?.response?.status;
            setSearchError([403, 404, 503].includes(status) ? 'unavailable' : 'generic');
            setResults([]);
        } finally {
            if (!controller.signal.aborted) {
                setIsSearching(false);
            }
        }
    }, [apiService]);

    // Debounce de invoer; onder de minimumlengte tonen we weer de gewone lijst
    useEffect(() => {
        if (!isActive || !fullTextEnabled || trimmedQuery.length < 2) {
            abortRef.current?.abort();
            setResults(null);
            setIndexStatus('ready');
            setSearchError(null);
            setIsSearching(false);
            return undefined;
        }

        const timer = setTimeout(() => runSearch(trimmedQuery, false), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [trimmedQuery, isActive, fullTextEnabled, runSearch]);

    // Zolang de zoekindex wordt opgebouwd stil opnieuw proberen tot status "ready"
    useEffect(() => {
        if (!isActive || !fullTextEnabled || indexStatus !== 'building' || searchError) {
            return undefined;
        }
        const timer = setTimeout(() => runSearch(trimmedQuery, true), BUILDING_POLL_MS);
        return () => clearTimeout(timer);
    }, [isActive, fullTextEnabled, indexStatus, searchError, trimmedQuery, runSearch, results]);

    useEffect(() => () => abortRef.current?.abort(), []);

    const kindLabel = (match: RecordingSearchMatch) => {
        switch (match.kind) {
            case 'title':
                return t('recordingsPage.search.kind.title');
            case 'transcript':
                return t('recordingsPage.search.kind.transcript');
            case 'summary':
                return t('recordingsPage.search.kind.summary');
            case 'supporting_document':
                return t('recordingsPage.search.kind.supportingDocument');
            case 'tag':
                return t('recordingsPage.search.kind.tag');
            default:
                return match.kind;
        }
    };

    const formatResultMeta = (recording: Recording) => {
        const date = new Date(recording.recordingDateTime ?? recording.createdAt);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        const dateLabel = date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
        const timeLabel = date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
        return `${dateLabel} · ${timeLabel}`;
    };

    const handleSelectSuggestion = (tagId: number) => {
        onToggleTag(tagId);
        // Token-gedrag: is de tag via de zoekterm gevonden, dan is die term "verbruikt"
        if (trimmedQuery !== '') {
            setQuery('');
            inputRef.current?.focus();
        }
    };

    // Ingeklapt neemt zoeken geen ruimte in; het zoekicoon staat in de paginakop
    if (!isOpen) {
        return null;
    }

    return (
        <div className="recordings-search-panel space-y-4">
            <div className="animate-slide-up relative">
                    <svg
                        className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-4.65a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        ref={inputRef}
                        type="search"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Escape') {
                                closeSearch();
                            }
                        }}
                        placeholder={t('recordingsPage.search.placeholder')}
                        aria-label={t('recordingsPage.search.placeholder')}
                        className="w-full rounded-xl border border-primary-100 bg-container py-3 pl-11 pr-11 text-base outline-none transition-colors focus:border-primary-600"
                        style={{ color: 'var(--text-strong)' }}
                    />
                    {isSearching ? (
                        <svg className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        // Eén knop met twee stappen: eerst de zoekterm wissen, daarna de balk sluiten
                        <button
                            onClick={() => (query ? setQuery('') : closeSearch())}
                            aria-label={query ? t('recordingsPage.search.clear') : t('recordingsPage.search.close')}
                            className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition-colors hover:bg-primary-600/[0.06]"
                            style={{ color: 'var(--text-muted)' }}
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
            </div>

            {/* Tagfilter: geselecteerde tags + suggesties op basis van de zoekterm */}
            {(selectedTags.length > 0 || tagSuggestions.length > 0) && (
                <div className="animate-fade-in flex flex-wrap items-center gap-2 rounded-xl border border-primary-100 bg-container px-4 py-2.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                        {t('recordingsPage.tagFilter.label')}
                    </span>

                    {selectedTags.map(tag => (
                        <button
                            key={tag.id}
                            onClick={() => onToggleTag(tag.id)}
                            title={t('recordingsPage.tagFilter.remove', { name: tag.name })}
                            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90"
                            style={{ backgroundColor: tag.color }}
                        >
                            <span>{getTagDisplayName(tag.name)}</span>
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    ))}

                    {tagSuggestions.map(({ tag, count }) => (
                        <button
                            key={tag.id}
                            onClick={() => handleSelectSuggestion(tag.id)}
                            title={t('recordingsPage.tagFilter.toggle', { name: tag.name })}
                            className="inline-flex items-center gap-1.5 rounded-full border border-primary-100 bg-paper px-2.5 py-1 text-xs font-medium transition-colors hover:border-primary-200"
                            style={{ color: 'var(--text)' }}
                        >
                            <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: tag.color }} aria-hidden="true" />
                            <span>{getTagDisplayName(tag.name)}</span>
                            <span className="text-[0.68rem] font-semibold" style={{ color: 'var(--text-muted)' }}>{count}</span>
                        </button>
                    ))}

                    {hiddenTagCount > 0 && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {t('recordingsPage.tagFilter.more', { count: hiddenTagCount })}
                        </span>
                    )}
                </div>
            )}

            {isActive && (
                <>
                    {/* Dagfilter vanuit de kalender; de banner van de pagina is tijdens het zoeken verborgen */}
                    {selectedDayLabel && (
                        <div className="flex items-center justify-between rounded-xl border border-primary-100 bg-container px-4 py-2.5 text-sm animate-fade-in">
                            <span style={{ color: 'var(--text)' }}>
                                <Trans
                                    i18nKey="recordingsPage.filteredOn"
                                    values={{ day: selectedDayLabel }}
                                    components={{ strong: <strong style={{ color: 'var(--text-strong)' }} /> }}
                                />
                            </span>
                            <button
                                onClick={onClearDate}
                                className="font-semibold text-primary-700 hover:underline"
                            >
                                {t('recordingsPage.showAll')}
                            </button>
                        </div>
                    )}

                    {indexStatus === 'building' && !searchError && (
                        <div
                            className="animate-fade-in rounded-xl border px-4 py-3 text-sm"
                            style={{ background: 'var(--warning-bg)', borderColor: 'transparent', color: 'var(--warning)' }}
                            role="status"
                        >
                            {t('recordingsPage.search.building')}
                        </div>
                    )}

                    {searchError && (
                        <div
                            className="animate-fade-in rounded-xl border px-4 py-3 text-sm"
                            style={{ background: 'var(--danger-bg)', borderColor: 'transparent', color: 'var(--danger)' }}
                            role="alert"
                        >
                            {searchError === 'unavailable'
                                ? t('recordingsPage.search.unavailable')
                                : t('recordingsPage.search.error')}
                        </div>
                    )}

                    {displayResults !== null && displayResults.length === 0 && !isSearching && (
                        <div className="animate-fade-in rounded-xl border border-primary-100 bg-container p-10 text-center">
                            {/* Zijn er wel resultaten maar niet op de gekozen dag, dan is de dag het probleem */}
                            {visibleResults !== null && visibleResults.length > 0 ? (
                                <>
                                    <h3 className="text-lg font-semibold" style={{ color: 'var(--text-strong)' }}>
                                        {t('recordingsPage.empty.titleForDay')}
                                    </h3>
                                    <p className="mt-2" style={{ color: 'var(--text-muted)' }}>
                                        {t('recordingsPage.empty.bodyForDay')}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3 className="text-lg font-semibold" style={{ color: 'var(--text-strong)' }}>
                                        {t('recordingsPage.search.noResults.title')}
                                    </h3>
                                    <p className="mt-2" style={{ color: 'var(--text-muted)' }}>
                                        {t('recordingsPage.search.noResults.body', { query: trimmedQuery })}
                                    </p>
                                </>
                            )}
                        </div>
                    )}

                    {displayResults !== null && displayResults.length > 0 && (
                        <section className="animate-slide-up overflow-hidden rounded-xl border border-primary-100 bg-container">
                            <h2 className="border-b border-primary-100 px-5 py-3.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                                {t('recordingsPage.search.resultCount', { count: displayResults.length })}
                            </h2>
                            <div className="divide-y divide-primary-100">
                                {displayResults.map(({ recording, matches }) => {
                                    const meta = formatResultMeta(recording);
                                    // Naam en tags staan al in de tile zelf; alleen content-snippets
                                    // tonen, en per bron (verslag/transcript/document) maximaal één.
                                    const seenKinds = new Set<string>();
                                    const visibleMatches: RecordingSearchMatch[] = [];
                                    for (const match of matches) {
                                        if (!match.snippet || match.kind === 'title' || match.kind === 'tag') continue;
                                        if (seenKinds.has(match.kind)) continue;
                                        seenKinds.add(match.kind);
                                        visibleMatches.push(match);
                                        if (visibleMatches.length >= MAX_VISIBLE_SNIPPETS) break;
                                    }

                                    return (
                                        <div
                                            key={recording.id}
                                            onClick={() => onOpenRecording(recording)}
                                            className="cursor-pointer px-5 py-4 transition-colors duration-200 hover:bg-primary-600/[0.04]"
                                        >
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="truncate text-base font-medium" style={{ color: 'var(--text-strong)' }}>
                                                        {recording.name || t('recordingsPage.unnamedRecording')}
                                                    </h3>
                                                    {meta && (
                                                        <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                                                            {meta}
                                                        </p>
                                                    )}
                                                </div>
                                                <svg className="h-4 w-4 flex-none text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>

                                            {visibleMatches.length > 0 && (
                                                <div className="mt-3 space-y-2.5">
                                                    {visibleMatches.map((match, index) => (
                                                        <div key={index} className="border-l-2 border-primary-100 pl-3">
                                                            <p
                                                                className="text-[11px] font-semibold uppercase tracking-wider"
                                                                style={{ color: 'var(--text-muted)' }}
                                                            >
                                                                {kindLabel(match)}
                                                            </p>
                                                            <p
                                                                className="mt-0.5 text-sm leading-relaxed"
                                                                style={{
                                                                    color: 'var(--text)',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                {renderSnippet(match.snippet)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {recording.tags && recording.tags.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    {recording.tags.slice(0, 3).map(tag => (
                                                        <button
                                                            key={tag.id}
                                                            onClick={event => {
                                                                event.stopPropagation();
                                                                onToggleTag(tag.id);
                                                            }}
                                                            aria-pressed={selectedTagIds.includes(tag.id)}
                                                            title={t('recordingsPage.tagFilter.toggle', { name: tag.name })}
                                                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
                                                            style={{
                                                                backgroundColor: tag.color,
                                                                boxShadow: selectedTagIds.includes(tag.id)
                                                                    ? `0 0 0 1.5px var(--container), 0 0 0 3px ${tag.color}`
                                                                    : undefined,
                                                            }}
                                                        >
                                                            {getTagDisplayName(tag.name)}
                                                        </button>
                                                    ))}
                                                    {recording.tags.length > 3 && (
                                                        <span className="inline-flex items-center rounded-full border border-primary-100 px-2 py-0.5 text-xs font-medium" style={{ background: 'var(--primary-50)', color: 'var(--text-muted)' }}>
                                                            +{recording.tags.length - 3}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
};

export default RecordingsSearch;
